import React, { FC, ReactElement, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { localizeType, Roles } from '../../commonType';
import { AvailableDates } from '../../features/offerAvailability/types';
import { bookReservation, bookReservationWithPayment } from '../../features/offerConfirmation/offerConfirmationSlice';
import BookingConfirmmationRequest, {
  AmountDetails,
  BillTo,
  ChargeDetails,
  ClientReferenceInformation,
  OrderInformation,
  PaymentSubmitRequest,
  ProspectInformation,
  RoomInformation,
  TokenInformation,
  // eslint-disable-next-line prettier/prettier
  Tour
} from '../../features/offerConfirmation/types';
import { RootState } from '../../redux/rootReducer';
import { getLoggedinUserName, numberToFixedPosition } from '../../util';
import { getStateCodeFromName, States_US } from '../../util/States';
import { LOC, PIN } from '../../util/strings';
import withLocalization from '../hoc/withLocalization';
import withRoles from '../hoc/withRoles';
import { getMaskedCardInformationFromToken } from '../OfferContactInformation/CyberSource/paymentUtil';
import './PackageSummaryReview.scss';

interface ConfirmationSelectorType {
  localize: localizeType;
  userRole: Roles;
}

const ConfirmationSelector: FC<ConfirmationSelectorType> = (props: ConfirmationSelectorType): ReactElement => {
  const { localize, userRole } = props;
  const [loading, setLoading] = useState(false);
  const SOURCE_NAME = userRole === Roles.Call_Center_Associate ? 'PBTA' : 'PBTF';
  const availabilityDates: AvailableDates = useSelector((state: RootState) => state.availabilityDates);
  const comments = useSelector((state: RootState) => state.confirmation.comments);
  const bonvoyValue = useSelector((state: RootState) => state.confirmation.bonvoyValue);
  const IIValue = useSelector((state: RootState) => state.confirmation.IIValue);
  const partnerId = useSelector((state: RootState) => state.confirmation.PartnerId);
  const partnerCode = useSelector((state: RootState) => state.confirmation.PartnerCode);
  const holdSessionId = availabilityDates.holdSessionId;
  const offerDetails = useSelector((state: RootState) => state.offerDetails);
  const offer = offerDetails.currentOffer;
  const selectedDate = availabilityDates.finalSelectedDate;
  const guests = offerDetails.guests;
  const prospect = offerDetails.prospect;
  const tour = selectedDate ? selectedDate.tour : undefined;
  const extraNights = availabilityDates.finalSelectedExtraNights;
  const merchantGroupId = process.env.REACT_APP_MERCHANT_GROUPID ? process.env.REACT_APP_MERCHANT_GROUPID : '';
  const dispatch = useDispatch();
  let prospectStateName: string =
    prospect?.stateProvince && prospect.stateProvince.length > 0 ? prospect.stateProvince : '';

  const hasProspectState = prospectStateName.length > 0;
  prospectStateName = hasProspectState
    ? prospectStateName.length > 2
      ? States_US.filter((e) => e.name === prospectStateName)[0].code
      : prospectStateName
    : '';
  const jwtPaymentToken = useSelector((state: RootState) => state.confirmation.transientTokenJwt);
  const userName = getLoggedinUserName();
  const calculateDays = (initialDays: string, additionalNights?: number): string => {
    if (initialDays) {
      let caculatedDuration = parseInt(initialDays, 10);
      if (additionalNights && additionalNights > 0) {
        caculatedDuration += additionalNights;
      }

      return caculatedDuration.toString();
    }
    return '';
  };

  async function onBookNow(evt) {
    setLoading(true);
    const ProspectInformation: ProspectInformation = {
      firstName: prospect && prospect.firstName ? prospect.firstName : '',
      lastName: prospect && prospect.lastName ? prospect.lastName : '',
      firstName2: prospect && prospect.spouseFirstName ? prospect.spouseFirstName : '',
      lastName2: prospect && prospect.spouseLastName ? prospect.spouseLastName : '',
      line1: prospect && prospect?.addressLine1 ? prospect?.addressLine1 : '',
      city: prospect && prospect?.city ? prospect?.city : '',
      stateProvince: prospectStateName,
      postalCode: prospect && prospect?.postalCode ? prospect?.postalCode : '',
      telephoneNumber: prospect && prospect?.phone ? prospect?.phone : '',
      emailAddress: prospect && prospect.email ? prospect.email : '',
      marriottBonvoyID: bonvoyValue,
      intervalID: IIValue,
      partnerID: partnerId,
      partnerCode: partnerCode,
    };
    const effortCode = offerDetails?.effortCode ? offerDetails.effortCode : undefined;
    const RoomInformation: RoomInformation = {
      accomodationCode: holdSessionId ? (selectedDate ? selectedDate.roomInformation.accomodationCode : '') : '',
      facilityPropertyID: holdSessionId
        ? selectedDate && selectedDate.roomInformation.FacilityPropertyID
          ? selectedDate.roomInformation.FacilityPropertyID
          : ''
        : '',
      miniHotelCode: holdSessionId ? (selectedDate ? selectedDate.roomInformation.miniHotelCode : '') : '',
      propertyCode: holdSessionId ? (selectedDate ? selectedDate.roomInformation.marshaCode : '') : '',
      rateCode: holdSessionId ? (selectedDate ? selectedDate.roomInformation.rateCode : '') : '',
      roomPoolCode: holdSessionId ? (selectedDate ? selectedDate.roomInformation.roomPoolCode : '') : '',
      roomTypeCode: holdSessionId
        ? selectedDate && selectedDate.roomInformation.RoomTypeCode
          ? selectedDate.roomInformation.RoomTypeCode
          : ''
        : '',
      bookingRoomType: holdSessionId
        ? selectedDate && selectedDate.roomInformation.BookingRoomType
          ? selectedDate.roomInformation.BookingRoomType
          : ''
        : '',
      numberOfDays:
        offerDetails && offerDetails.selectedRoomType
          ? calculateDays(offerDetails.selectedRoomType?.numberOfDays, extraNights)
          : '',
      numberOfNights:
        offerDetails && offerDetails.selectedRoomType
          ? calculateDays(offerDetails.selectedRoomType?.numberOfNights, extraNights)
          : '',
      numberOfGuests:
        offerDetails && offerDetails.selectedRoomType ? offerDetails.selectedRoomType?.sleepCapacity.toString() : '',
      numberOfAdults: guests ? guests.adults.toString() : '',
      numberOfChildren: guests ? guests.children.toString() : '',
      preferences: holdSessionId
        ? selectedDate && selectedDate.roomInformation
          ? selectedDate.roomInformation.preferences
          : undefined
        : undefined,
    };

    const Tour: Tour = {
      offerId: offer ? offer.offerCode : '',
      tourType: tour ? tour.tourType : '',
      tourSiteCode: holdSessionId ? (tour ? tour.tourSiteCode : '') : '',
      salesCenterCode: holdSessionId ? (tour ? tour.salescenterCode : '') : '',
      appointmentDate: holdSessionId ? (tour ? tour.appointmentDate : '') : '',
    };
    const bookingReq: BookingConfirmmationRequest = {
      associateEID: userName,
      sourceName: SOURCE_NAME,
      effortCode: effortCode,
      holdSessionId: holdSessionId ? holdSessionId : '',
      pin: offerDetails.searchParameter.mode === PIN ? offerDetails.searchParameter.pin : '',
      loc: offerDetails.searchParameter.mode === LOC ? offerDetails.searchParameter.pin : '',
      startDate: holdSessionId
        ? selectedDate && selectedDate.checkInDate
          ? selectedDate.checkInDate.split('T')[0]
          : ''
        : '',
      endDate: holdSessionId
        ? selectedDate && selectedDate.checkOutDate
          ? selectedDate.checkOutDate.split('T')[0]
          : ''
        : '',
      holidaySurcharge: holdSessionId
        ? selectedDate && selectedDate.holidaySurcharge
          ? numberToFixedPosition(selectedDate.holidaySurcharge)
          : '0.00'
        : '',
      holidayName: holdSessionId ? (selectedDate && selectedDate.holidayName ? selectedDate.holidayName : '') : '',
      additionalNightCharge: holdSessionId
        ? selectedDate && selectedDate.additionalNightCharge
          ? numberToFixedPosition(selectedDate.additionalNightCharge)
          : '0.00'
        : '',
      totalCost: holdSessionId
        ? selectedDate && selectedDate.totalCost
          ? numberToFixedPosition(selectedDate.totalCost)
          : '0.00'
        : '',
      basePrice: holdSessionId
        ? selectedDate && selectedDate.basePrice
          ? numberToFixedPosition(selectedDate.basePrice)
          : '0.00'
        : '',
      retailPrice: holdSessionId
        ? selectedDate && selectedDate.retailPrice
          ? numberToFixedPosition(selectedDate.retailPrice)
          : '0.00'
        : '',
      additionalNights: extraNights ? extraNights : 0,
      tour: Tour,
      roomInformation: RoomInformation,
      prospectInformation: ProspectInformation,
      comments: comments,
      alternateLoc: offerDetails.alternateLOC,
    };
    if (userRole === Roles.Field_Associate) {
      const tokenInfo: TokenInformation = {
        transientTokenJwt: jwtPaymentToken ? jwtPaymentToken : '',
      };
      const customerId =
        offerDetails.searchParameter.mode === PIN
          ? offerDetails.searchParameter.pin
          : prospect
          ? prospect.lastName
            ? prospect.lastName.substr(0, 20)
            : ''
          : '';

      const selectedRoomDescription = offerDetails.selectedRoomType
        ? offerDetails.selectedRoomType.roomTypeDescription
        : '';
      const paymentId = `${customerId}-${new Date().toISOString()}-${selectedRoomDescription}`;
      const clientInformation: ClientReferenceInformation = {
        code: 'WEB_PBT',
        associate: true,
        customerId: customerId,
        merchantGroupId,
        paymentId: paymentId,
        referenceId: '',
        srcSystemName: 'WEB_PBT',
        transactionId: '',
        storeCard: true,
      };
      const amountDetails: AmountDetails = {
        totalAmount: holdSessionId
          ? selectedDate && selectedDate.totalCost
            ? numberToFixedPosition(selectedDate.totalCost)
            : '0.00'
          : offerDetails.selectedRoomType
          ? offerDetails.selectedRoomType.depositAmount.substr(1)
          : '0.00',
        currency: 'USD',
      };
      const billingInfo = offerDetails.billingInfo;
      const billTo: BillTo = {
        address1: billingInfo && billingInfo.addressLine1 ? billingInfo.addressLine1 : '',
        address2: billingInfo && billingInfo.unitNo ? billingInfo.unitNo : '',
        administrativeArea: billingInfo && billingInfo.stateProvince ? billingInfo.stateProvince : '',
        country: prospect && prospect.countryCode ? prospect.countryCode.substr(0, 2) : 'US',
        email: billingInfo && billingInfo.email ? billingInfo.email : '',
        firstName: billingInfo && billingInfo.firstName ? billingInfo.firstName : '',
        lastName: billingInfo && billingInfo.lastName ? billingInfo.lastName : '',
        locality: billingInfo && billingInfo.city ? billingInfo.city : '',
        postalCode: billingInfo && billingInfo.postalCode ? billingInfo.postalCode : '',
      };
      const orderInformation: OrderInformation = {
        amountDetails: amountDetails,
        billTo: billTo,
      };
      const cardSummary = getMaskedCardInformationFromToken(jwtPaymentToken ? jwtPaymentToken.toString() : '');
      const merchantID = process.env.REACT_APP_MERCHANTID ? process.env.REACT_APP_MERCHANTID.toString() : '';
      const chargeDetails: ChargeDetails = {
        billingAddressLine1: billingInfo && billingInfo.addressLine1 ? billingInfo.addressLine1 : '',
        billingEmailAddress: billingInfo && billingInfo.email ? billingInfo.email : '',
        billingFirstName: billingInfo && billingInfo.firstName ? billingInfo.firstName : '',
        billingLastName: billingInfo && billingInfo.lastName ? billingInfo.lastName : '',
        billingPhoneNumber: billingInfo && billingInfo.phone ? billingInfo.phone : '',
        billingCity: billingInfo && billingInfo.city ? billingInfo.city : '',
        billingCountryCode: 'US',
        billingStateProvinceCode:
          billingInfo && billingInfo.stateProvince ? getStateCodeFromName(billingInfo.stateProvince) : '',
        billingZip: billingInfo && billingInfo.postalCode ? billingInfo.postalCode : '',
        cardCheck: cardSummary.maskedCardNumber,
        currencyCode: 'USD',
        expirationDate: cardSummary.expiration,
        merchantID: merchantID,
        paymentMethodCode: cardSummary.cardType.toUpperCase(),
        preferredInvitationNumber: offerDetails.searchParameter.mode === PIN ? offerDetails.searchParameter.pin : '',
        totalAmount: holdSessionId
          ? selectedDate && selectedDate.totalCost
            ? numberToFixedPosition(selectedDate.totalCost)
            : '0.00'
          : offerDetails.selectedRoomType
          ? offerDetails.selectedRoomType.depositAmount.substr(1)
          : '0.00',
        merchantGroupId,
      };
      bookingReq.charge = chargeDetails;
      const paymentReq: PaymentSubmitRequest = {
        tokenInformation: tokenInfo,
        clientReferenceInformation: clientInformation,
        orderInformation: orderInformation,
      };
      dispatch(bookReservationWithPayment(paymentReq, bookingReq));
    } else {
      dispatch(bookReservation(bookingReq));
    }
  }

  return (
    <>
      <div className="eligibilityButton top40">
        <button className="checkInButton palmBg upperCase" onClick={onBookNow} disabled={loading}>
          {localize('preview-sales-engine.static-content.copy.Confirmation Page.book-now-button')}
        </button>
      </div>
    </>
  );
};

export default withRouter(withRoles(withLocalization(ConfirmationSelector)));
